import React, { createContext, useContext } from 'react';

import { getMeta } from '../../api';
import { useFetch } from '../../hooks';
import { useAuth } from '../../shared';
import { useEntries } from './useEntries';

type Cleanup = () => void;

type AppContextType = {
  loading: boolean;
  meta: AppMetaType;

  title: React.ReactNode | null;
  removeTitle: (id: string) => void;
  addTitle: (node: React.ReactNode) => Cleanup;

  refresh: () => void;
};

const AppContext = createContext<AppContextType | undefined>(undefined);

const defaultMeta: AppMetaType = {
  campaigns: [],
  campaignsMap: {},
  campaignsData: [],
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();

  const {
    data,
    refresh,
    initializing: loading,
  } = useFetch<AppMetaType>(user ? getMeta : () => defaultMeta, [user]);
  const [titles, addTitle, removeTitle] = useEntries<React.ReactNode>();

  const contextValue = React.useMemo<AppContextType>(
    () => ({
      loading,

      refresh,
      addTitle,
      removeTitle,

      meta: data || defaultMeta,
      title: titles.at(-1)?.value || null,
    }),
    [loading, data, titles, addTitle, removeTitle, refresh]
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
